import type { CrmNoteType } from '../prisma/types.ts';

export const SrPartialPackingTypeMap = {
    ALWAYS_ROUND_DOWN: 'ALWAYS_ROUND_DOWN',
    ALWAYS_ROUND_UP: 'ALWAYS_ROUND_UP',
    ONLY_ROUND_DOWN: 'ONLY_ROUND_DOWN',
    ONLY_ROUND_UP: 'ONLY_ROUND_UP',
    NO_PACKAGE_BREAK: 'NO_PACKAGE_BREAK',
    UNKNOWN: 'UNKNOWN',
    NO_RESTRICTION: 'NO_RESTRICTION',
} as const;

export type SrPartialPackingTypeMap = (typeof SrPartialPackingTypeMap)[keyof typeof SrPartialPackingTypeMap];

export function isUnbreakablePackageType(type: SrPartialPackingTypeMap): boolean {
    // TODO: this logic is almost certainly wrong but was moved here during a refactor
    switch (type) {
        case 'ONLY_ROUND_DOWN':
        case 'NO_PACKAGE_BREAK':
            return true;
        default:
            return false;
    }
}

export const SrSalesOrderType = {
    ORDER: 'ORDER',
    CREDIT_NOTE: 'CREDITNOTE',
    QUOTE: 'QUOTE',
    RETURN: 'RETURN',
    CONTRACT: 'CONTRACT',
    DEBIT_MEMO_REQUEST: 'DEBIT_MEMO_REQUEST',
    CREDIT_MEMO_REQUEST: 'CREDIT_MEMO_REQUEST',
} as const;

export type SrSalesOrderType = (typeof SrSalesOrderType)[keyof typeof SrSalesOrderType];

export const SrOrderStatus = {
    OPEN: 'OPEN',
    PARTIALLY_BILLED: 'PARTIALLY_BILLED',
    BILLED: 'BILLED',
    OPEN_IN_PICKING: 'OPEN_IN_PICKING',
    PROCESSED_INTERNALLY: 'PROCESSED_INTERNALLY',
    CANCELLED: 'CANCELLED',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    RECEIVED: 'RECEIVED',
    LOST: 'LOST',
    REVISED: 'REVISED',
    COMPLETED: 'COMPLETED',
    UNKNOWN: 'UNKNOWN',
    RELEASED_FOR_DELIVERY: 'RELEASED_FOR_DELIVERY',
    RETURNED: 'RETURNED',
    SHIPPED: 'SHIPPED',
    PARTIALLY_PICKED: 'PARTIALLY_PICKED',
    PICKED: 'PICKED',
    PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
    PENDING: 'PENDING',
    OTHER: 'OTHER',
} as const;

export type SrOrderStatus = (typeof SrOrderStatus)[keyof typeof SrOrderStatus];

export const SrOrderLineStatus = {
    OPEN: 'OPEN',
    OPEN_IN_PICKING: 'OPEN_IN_PICKING',
    RELEASED_FOR_DELIVERY: 'RELEASED_FOR_DELIVERY',
    RELEASED_FOR_BILLING: 'RELEASED_FOR_BILLING',
    BILLED: 'BILLED',
    PROCESSED_INTERNALLY: 'PROCESSED_INTERNALLY',
    CANCELLED: 'CANCELLED',
    UNKNOWN: 'UNKNOWN',
    RETURNED: 'RETURNED',
    SHIPPED: 'SHIPPED',
    PARTIALLY_BILLED: 'PARTIALLY_BILLED',
    PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    PENDING: 'PENDING',
    RECEIVED: 'RECEIVED',
    LOST: 'LOST',
    OTHER: 'OTHER',
    WON: 'WON',
} as const;

export type SrOrderLineStatus = (typeof SrOrderLineStatus)[keyof typeof SrOrderLineStatus];

export const SrCustomerAddressType = {
    SHIPPING: 'SHIPPING',
    BILLING: 'BILLING',
    PRIMARY: 'PRIMARY',
} as const;

export type SrCustomerAddressType = (typeof SrCustomerAddressType)[keyof typeof SrCustomerAddressType];

export const CustomerOrderActivityStatus = {
    NEW: 'new',
    ACTIVE: 'active',
    AT_RISK: 'at_risk',
    DORMANT: 'dormant',
} as const;

export type CustomerOrderActivityStatus =
    (typeof CustomerOrderActivityStatus)[keyof typeof CustomerOrderActivityStatus];

export const CustomerBusinessStatus = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
} as const;

export type CustomerBusinessStatus = (typeof CustomerBusinessStatus)[keyof typeof CustomerBusinessStatus];

export const SrErpCustomerStatus = {
    NORMAL: 'NORMAL',
    BLOCKED: 'BLOCKED',
} as const;

export type SrErpCustomerStatus = (typeof SrErpCustomerStatus)[keyof typeof SrErpCustomerStatus];

export const SrCustomerStatus = {
    ...CustomerOrderActivityStatus,
    ...CustomerBusinessStatus,
    BLOCKED: 'blocked',
} as const;

export type SrCustomerStatus = (typeof SrCustomerStatus)[keyof typeof SrCustomerStatus];

export const SrInsightType = {
    CHURN_WARNING: 'ChurnWarningWebCategory',
    GLOBAL_CHURN_WARNING: 'GlobalChurnWarning',
    CROSS_SELL: 'XSellWebCategory',
    FIRST_PURCHASE: 'FirstPurchaseWebCat3',
    SHARE_OF_WALLET: 'ShareOfWalletCategory',
    PREVIOUSLY_PURCHASED: 'PreviouslyPurchasedCategory',
    INDUSTRY_TOP: 'IndustryTopCategory',
    GLOBAL_TOP: 'GlobalTopCategory',
} as const;

export type SrInsightType = (typeof SrInsightType)[keyof typeof SrInsightType];

export type OrderLineFacts = {
    _order_line_transaction_key: number;
    _customer_key: number | null;
    _product_variant_key: number | null;
    _sales_rep_key_1: number | null;
    _sales_rep_key_2: number | null;
    _sales_rep_key_3: number | null;
    _order_date_key: number | null;
    _order_creation_date_key: number | null;
    _order_release_date_key: number | null;
    _order_line_delivery_date_key: number | null;
    _order_line_invoice_date_key: number | null;
    _clerk_key: number | null;
    order_id: string | null;
    customer_id: string | null;
    order_currency_id: string | null;
    order_currency_rate: number | null;
    order_delivery_address_id: string | null;
    order_line_delivered_quantity: number | null;
    order_line_gross_margin: number | null;
    order_line_line_item_id: string | null;
    order_line_line_item_status: string | null;
    order_line_order_quantity: number | null;
    order_line_ship_from_id: string | null;
    order_line_status: string | null;
    order_line_total_amount: number | null;
    order_line_total_amount_hd: number | null;
    order_line_unit_price: number | null;
    order_quote_sales_rep_id: string | null;
    order_ship_from_id: string | null;
    order_status: string | null;
    order_line_product_variant_id: string | null;
    order_line_quantity_uom: string;
    product_variant_name: string | null;
};

export type CustomerDimension = {
    customer_id: string | null;
    customer_class: string | null;
    customer_name: string | null;
    customer_group_id: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    address_line_3: string | null;
    street: string | null;
    zip_code: string | null;
    city: string | null;
    country_code: string | null;
    industry_sector: string | null;
    phone_number: string | null;
    mobile_number: string | null;
    fax_number: string | null;
    _customer_key: string;
    company_id: string | null;
    sales_region: string | null;
    status: string | null;
};

export type DateDimention = {
    _date_key: number;
    date: Date | null;
    year: number | null;
    month: number | null;
    day_of_month: number | null;
    quarter: number | null;
    day_of_week: number | null;
    day_of_year: number | null;
    week_of_year: number | null;
    is_weekend: boolean | null;
};

export type ProductDimention = {
    _product_key: string;
    _seq_key: number | null;
    product_id: string | null;
    product_name: string | null;
    description_short: string | null;
    description: string | null;
    updated_at: string | null;
    _meta_variant_id: string | null;
};

export type ProductVariantDimention = {
    _product_variant_key: number;
    _seq_key: number | null;
    product_variant_id: string | null;
    product_id: string | null;
    product_variant_name: string | null;
    status: string | null;
    is_active: number | null;
    article_group_id: string | null;
    article_group_name: string | null;
    article_main_group_name: string | null;
    quantity_unit_storage: string | null;
    quantity_unit_sales: string | null;
    factor_sales_unit: number | null;
    retail_price: number | null;
    gtin: string | null;
    ean: string | null;
    price_scale_factor: number | null;
    package_unit: string | null;
    sales_units_per_package_unit: number | null;
    partial_packing_type: SrPartialPackingTypeMap | null;
    parts_list_type: string | null;
    supplier_id: string | null;
    supplier_name: string | null;
    purchase_order_number: string | null;
    image_path: string | null;
    updated_at: string | null;
};

export type SalesRepDimension = {
    sales_rep_id: string | null;
    sales_rep_name: string | null;
    is_internal: number | null;
    is_inactive: number | null;
    _sales_rep_key: number;
};

export type AppCustomerAddresses = {
    id: string;
    _seq_key: number | null;
    is_default: boolean | null;
    type: SrCustomerAddressType | null;
    customer_id: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    address_line_3: string | null;
    post_box: string | null;
    zip_code_post_box: string | null;
    street: string | null;
    zip_code: string | null;
    city: string | null;
    country_code: string | null;
    country: string | null;
};

export type AppCustomerContacts = {
    id: string;
    _seq_key: number | null;
    customer_id: string | null;
    salutation: string | null;
    name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
    mobile_number: string | null;
    fax_number: string | null;
};

export type AppProductParts = {
    id: string;
    _seq_key: number | null;
    part_list_article_id: string | null;
    contained_article_id: string | null;
    variant_id: string | null;
    quantity: string | null;
    quantity_scale_factor: number | null;
    quantity_unit_storage: string | null;
};

export type AppSalesOrders = {
    id: string;
    _seq_key: number | null;
    sales_rep_id_1: string | null;
    sales_rep_id_2: string | null;
    sales_rep_id_3: string | null;
    customer_id: string | null;
    contact_id: string | null;
    customer_contact_name: string | null;
    storage_id: string | null;
    type: SrSalesOrderType | null;
    sales_order_type: string | null;
    status: SrOrderStatus | null;
    clerk_id: string | null;
    clerk_id_entry: string | null;
    order_text: string | null;
    entry_date: Date | null;
    order_date: Date | null;
    shipping_type_id: string | null;
    delivery_type_id: string | null;
    delivery_address_id: string | null;
    delivery_address_line_1: string | null;
    delivery_address_line_2: string | null;
    delivery_address_line_3: string | null;
    delivery_address_street: string | null;
    delivery_address_zip_code: string | null;
    delivery_address_city: string | null;
    delivery_address_country_code: string | null;
    billing_address_line_1: string | null;
    billing_address_line_2: string | null;
    billing_address_line_3: string | null;
    billing_address_street: string | null;
    billing_address_post_box: string | null;
    billing_address_zip_post_box: string | null;
    billing_address_zip_code: string | null;
    billing_address_city: string | null;
    billing_address_country_code: string | null;
    billing_address_country: string | null;
    updated_at: Date | null;
    po_number: string | null;
};

export type AppSalesOrdersMV = {
    id: string | null;
    clerk_id: string | null;
    clerk_name: string | null;
    customer_id: string | null;
    customer_name: string | null;
    company_id: string | null;
    company_name: string | null;
    display_id: string | null;
    display_identifier: string | null;
    document_type_name: string | null;
    document_type: string | null;
    entry_date: Date | null;
    line_items_count: string | null;
    order_date: Date | null;
    po_number: string | null;
    sales_order_id: string | null;
    sales_rep_ids: string | null;
    status: string | null;
    storage_id: string | null;
    total_amount_net: number | null;
    total_margin: number | null;
    type: SrSalesOrderType | null;
};

export type AppSalesReps = {
    id: string;
    _seq_key: number | null;
    name: string | null;
    is_internal: number | null;
    is_inactive: number | null;
};

export type AppSalesRepCustomers = {
    id: string;
    _seq_key: number | null;
    customer_id: string | null;
    sales_rep_id: string | null;
    type: string | null;
};

export type AppShippingTypes = {
    id: string;
    _seq_key: number | null;
    name: string | null;
    dispatcher: string | null;
};

export type AppStorageGroups = {
    id: string;
    _seq_key: number | null;
    name: string | null;
};

export type AppStorageLocations = {
    id: string;
    _seq_key: number | null;
    group_id: string | null;
    name: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    address_line_3: string | null;
    street: string | null;
    zip_code: number | null;
    city: string | null;
    country_code: string | null;
};

export type AppStorageStockLevels = {
    id: string;
    _seq_key: number | null;
    product_variant_id: string | null;
    storage_id: string | null;
    stock_quantity: number | null;
    reserved: number | null;
    picked: number | null;
    available: number | null;
    effective: number | null;
};

export type CustomerProductCategoryChurnWarningsFacts = {
    _customer_category_churn_warning_key: string;
    customer_id: string;
    product_category_id: string;
    product_category_name: string;
    recency: number;
    days_between_order_threshold_80: number;
    days_between_order_threshold_85: number;
    days_between_order_threshold_90: number;
    days_between_order_threshold_95: number;
    days_between_order_mean: number;
    days_between_order_median: number;
    churn_warning_at_80: number;
    churn_warning_at_85: number;
    churn_warning_at_90: number;
    churn_warning_at_95: number;
    rank: number;
    total_spent_last_year: number;
};

export type AppCategorys = {
    id: string;
    _seq_key: number | null;
    title: string | null;
    parent_id: string | null;
    depth: number | null;
    breadcrumbs: string | null;
};

export type AppProductsCategories = {
    id: string;
    _seq_key: number | null;
    category_id: string | null;
    product_variant_id: string | null;
};

export type AppSalesOrderTypes = {
    id: string;
    _seq_key: number | null;
    type: string | null;
    name: string | null;
};

export type AppRepeatPurchasesRecommendation = {
    _customer_repeat_purchase_recommendation_key: string;
    customer_id: number;
    product_id: number;
    rank: number;
    number_of_unique_orders: number;
    last_order_date: string;
    product_variant_id_most_common: string;
    product_variant_id_most_recent: string;
    recency_days: number;
    recency_weeks: string;
    recency_comparison_date: string;
    r2: number;
    shape: number;
    loc: number;
    scale: number;
    mode_days: number;
    RAi: number;
    QAi: number;
    PAi: number;
};

export type AppInsight = {
    id: string;
    insight_type: SrInsightType | null;
    grouping_entity_id: string | null;
    grouping_entity_type: string | null;
    actionable_entity_id: string | null;
    actionable_entity_type: string | null;
    absolute_number: number | null;
    percentage_number: number | null;
    reason: string | null;
    generated_at: Date | null;
    reason_locale: string | null;
    batch_id: string | null;
    template_arguments_json: string | null;
    expanded_view_arguments_json: string | null;
    rank: number | null;
    aggregated_at: Date | null;
    aggregated_id: string | null;
    first_seen_at: Date | null;
    version: string | null;
};

export type AppUsedInsight = {
    insight_id: string;
    reaction: string | null;
    used_at: string | null;
    hidden_at: string | null;
    notes: string | null;
    reason: string | null;
};

export type ClerkDimension = {
    _clerk_key: string;
    clerk_id: string | null;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    full_name: string | null;
};

export type CompanyDimension = {
    _company_key: string;
    company_id: string | null;
    company_name: string | null;
};

export type ProductRankingsByyIndustryAndCategoryFacts = {
    customer_segment_id: string | null;
    customer_segment_column: string | null;
    product_variant_id: string | null;
    product_variant_name: string | null;
    product_category_id: string | null;
    product_category_name: string | null;
    order_count: number | null;
    total_revenue: number | null;
    ranking_method: string | null;
    rank: number | null;
};

export type PersonalisedInsightItemRecommendations = {
    insight_id: string;
    product_variant_id: string;
    aggregated_id: string;
    insight_type: string;
    grouping_entity_id: string;
    actionable_entity_id: string;
    recommendation_source: string;
    product_variant_name: string;
    rank: number;
    source_batch_id: string;
    score: number;
    aggregated_at: string;
    rank_brand_corrected: number | null;
    // Declared BOOLEAN in StarRocks, but this driver returns it as 0/1, not a real JS boolean.
    is_preferred_brand: number | null;
    brand: string | null;
    // JSON with p25_unit_price / p75_unit_price (and min, median, max, avg_order_quantity, total_quantity_purchased, customer_count, order_line_count).
    price_information_json: string | object | null;
};

export type AppProductsCategoriesBridge = {
    product_variant_id: string;
    product_category_id: string;
    parent_product_category_id: string;
    product_category_name: string;
    depth: number | null;
    depth_from_parent: number | null;
    parent_product_category_name: string;
};

export type RelatedItemRecommendation = {
    product_variant_id: string | null;
    recommended_product_variant_id: string | null;
    rank: number | null;
    score: number | null;
    source: string;
    created_at: string;
};

export type ChurnWarningsProducts = {
    id: string;
    customer_id: string;
    product_category_id: string;
    product_variant_id: string;
    batch_id: string;
    generated_at: string;
    display_product: number;
    brand: string | null;
    is_house_brand: number | null;
};

export type AiPlatoOrderActivity = {
    customer_id: string;
    days_since_first_order: number | null;
    days_since_last_order: number | null;
    revenue_730_365: number | null;
    revenue_365_0: number | null;
    order_count_730_365: number | null;
    order_count_365_0: number | null;
    churn: number | null;
    year_to_year_revenue: number | null;
    plato_order_activity_status: CustomerOrderActivityStatus | null;
};

export type OrderAndInvoiceLineDailyRevenue = {
    date: Date | null;
    _date_key: number | null;
    order_line_status: string | null;
    article_group_id: string | null;
    article_group_name: string | null;
    clerk_id: string | null;
    clerk_fullname: string | null;
    customer_id: string | null;
    customer_name: string | null;
    industry_sector: string | null;
    order_type: string | null;
    product_variant_id: string | null;
    product_variant_name: string | null;
    sales_rep_ids: string | null;
    storage_location_id: string | null;
    storage_location_name: string | null;
    supplier_id: string | null;
    supplier_name: string | null;
    document_source: 'INVOICE' | 'ORDER' | null;
    daily_total_amount: number | null;
    daily_gross_margin: number | null;
    daily_units: number | null;
};

export type InvoiceLineFacts = {
    _transaction_key: string;
    _sales_order_item_key: string | null;
    _customer_key: string | null;
    _product_variant_key: string | null;
    _clerk_key: string | null;
    _order_date_key: string | null;
    _invoice_line_date_key: string | null;
    _order_creation_date_key: string | null;
    _order_release_date_key: string | null;
    customer_id: string | null;
    order_id: string | null;
    order_line_id: string | null;
    order_status: string | null;
    order_line_status: string | null;
    order_currency_id: string | null;
    order_currency_rate: number | null;
    order_line_product_variant_id: string | null;
    product_variant_name: string | null;
    order_line_order_quantity: number | null;
    order_line_unit_price: number | null;
    total_amount: number | null;
    total_amount_hd: number | null;
    gross_margin: number | null;
    gross_margin_hd: number | null;
    transaction_type: string | null;
};

export type CurrencyDimension = {
    _currency_key: string;
    currency_id: string | null;
    currency_iso_code: string | null;
    currency_name: string | null;
    is_default: 0 | 1 | null;
};

export type CustomerTopProductsQ2C = {
    customer_id: string;
    product_variant_id: string;
    product_id: string | null;
    order_count: number | null;
    rank: number | null;
    article_group_id: string | null;
    article_group_name: string | null;
    package_unit: string | null;
    partial_packing_type: SrPartialPackingTypeMap | null;
    product_variant_name: string;
    quantity_unit_sales: string | null;
    sales_units_per_package_unit: number | null;
    batch_id: string | null;
    generated_at: string | null;
};

export type CrmNote = {
    note_id: string;
    note_type: CrmNoteType;
    customer_id: string;
    created_by: string;
    occurred_at: Date;
    created_at: Date;
    updated_at: Date;
    event_id: string | null;
    erp_note_id: string | null;
    interaction_happened: boolean | null;
};

export type CrmCustomer = {
    tenant_id: string;
    erp_customer_id: string;
    business_status: string;
    created_at: Date;
    updated_at: Date;
};

export type PlatoUser = {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    erp_user_id: string | null;
    created_at: Date;
    updated_at: Date;
    sales_rep_assignment_ids: string;
    user_role: string;
};

export type InsightOutcome = {
    id: string;
    erp_customer_id: string;
    actionable_entity_id: string;
    actionable_entity_type: string | null;
    insight_id: string;
    insight_type: string;
    insight: string | null;
    text: string | null;
    text_rich: string | null;
    status: string;
    response: string;
    created_by: string;
    tenant_id: string;
    created_at: Date;
    updated_at: Date;
};

export type DB = {
    ai_churn_warnings_products: ChurnWarningsProducts;
    ai_customer_top_products_q2c: CustomerTopProductsQ2C;
    ai_ins__insight: AppInsight;
    ai_personalised_insight_item_recommendations: PersonalisedInsightItemRecommendations;
    ai_plato_order_activity: AiPlatoOrderActivity;
    ai_product_rankings_by_industry_and_category_facts: ProductRankingsByyIndustryAndCategoryFacts;
    ai_related_item_recommendations: RelatedItemRecommendation;
    app_crm_customers: CrmCustomer;
    app_crm_notes: CrmNote;
    app_insight_outcome: InsightOutcome;
    app_users: PlatoUser;
    categories: AppCategorys;
    clerk_dim: ClerkDimension;
    company_dim: CompanyDimension;
    currency_dim: CurrencyDimension;
    customer_addresses: AppCustomerAddresses;
    customer_contacts: AppCustomerContacts;
    customer_dim: CustomerDimension;
    customer_product_category_churn_warnings_facts: CustomerProductCategoryChurnWarningsFacts;
    date_dim: DateDimention;
    ins__used_insight: AppUsedInsight;
    invoice_line_transaction_facts: InvoiceLineFacts;
    order_and_invoice_line_daily_revenue: OrderAndInvoiceLineDailyRevenue;
    order_line_transaction_facts: OrderLineFacts;
    product_dim: ProductDimention;
    product_parts: AppProductParts;
    product_variants_dim: ProductVariantDimention;
    product_web_category_bridge: AppProductsCategoriesBridge;
    products_categories: AppProductsCategories;
    repeat_purchases_recommendations: AppRepeatPurchasesRecommendation;
    sales_order_mv: AppSalesOrdersMV;
    sales_order_types: AppSalesOrderTypes;
    sales_orders: AppSalesOrders;
    sales_representative_dim: SalesRepDimension;
    sales_reps_customers: AppSalesRepCustomers;
    sales_reps: AppSalesReps;
    shipping_types: AppShippingTypes;
    storage_groups: AppStorageGroups;
    storage_locations: AppStorageLocations;
    storage_stock_levels: AppStorageStockLevels;
};
